<template>
  <FlotoDrawer width="55%" :open="isOpen" @hide="isOpen = false">
    <template v-slot:trigger>
      <a @click="handleOpen">
        <MButton
          id="download-btn"
          class="text-neutral admin-page-link"
          :aria-label="$t('downloads')"
        >
          <MIcon name="download-history" size="lg" />
        </MButton>
      </a>
    </template>

    <template v-slot:title>
      <span class="text-neutral">{{ $t('downloads') }}</span>
    </template>

    <div class="flex flex-1 flex-col">
      <FlotoCrudContainer
        ref="crudContainer"
        :fetch-fn="getDownloads"
        :columns="columns"
        as-table
      >
        <template v-slot:add-controls="{ refreshList }">
          <MRow class="mb-2">
            <MCol>
              <FlotoSearchBar
                :module-name="$constants.DOWNLOAD"
                :allow-save-search="false"
                :persist-criteria="false"
                prevent-supported-api
                @change="applyCriteria($event, refreshList)"
              />
            </MCol>
            <MCol class="flex justify-end items-center">
              <small
                v-if="filterCriteria.fromDate && filterCriteria.toDate"
                class="mx-1 text-light"
              >
                {{ filterCriteria.fromDate | datetime }} -
                {{ filterCriteria.toDate | datetime }}
              </small>
              <MButton
                shape="circle"
                variant="neutral-lighter"
                :shadow="false"
                class="mx-1"
                @click="showTimeFilter = !showTimeFilter"
              >
                <MIcon name="filter" size="md" />
              </MButton>
              <MButton
                shape="circle"
                variant="neutral-lighter"
                :shadow="false"
                class="mx-1"
                @click="refresh"
              >
                <MIcon name="sync" size="md" />
              </MButton>
            </MCol>
          </MRow>
          <FlotoExpand>
            <MCol v-if="showTimeFilter" :size="12" class="mt-1">
              <DownloadsFilter
                ref="downloadsFilter"
                class="mb-2"
                :value="filterCriteria"
                @change="handleFilterChange"
              />
            </MCol>
          </FlotoExpand>
        </template>
        <template v-slot:refModel="{ item }">
          <td class="text-ellipsis">
            {{
              item.downloadContext && item.downloadContext.parentModule
                ? `${$tc(item.downloadContext.parentModule)} -`
                : ''
            }}
            {{ $tc(item.refModel) || '---' }}
          </td>
        </template>
        <template v-slot:trackingId="{ item }">
          <td class="text-ellipsis">
            {{ item.trackingId || '---' }}
          </td>
        </template>
        <template v-slot:name="{ item }">
          <td class="text-ellipsis">
            {{ $tc(item.name) || '---' }}
          </td>
        </template>
        <template v-slot:downloadState="{ item }">
          <td class="text-ellipsis">
            {{ $tc(item.downloadState) || '---' }}
          </td>
        </template>
        <template v-slot:triggeredTime="{ item }">
          <td class="text-ellipsis">
            {{ item.triggeredTime | datetime }}
          </td>
        </template>
        <template v-slot:endTime="{ item }">
          <td class="text-ellipsis">
            {{ item.endTime | datetime }}
          </td>
        </template>
        <template v-slot:action="{ item }">
          <td>
            <span class="flex items-center">
              <FlotoDeleteBtn
                v-if="
                  item.downloadState === 'in_progress' ||
                  item.downloadState === 'in_queue'
                "
                only-icon
                :message="$t('confirm_cancel_download')"
                @confirm="handleCancleDownlod(item)"
              >
                <template v-slot:trigger>
                  <MTooltip>
                    <template v-slot:trigger>
                      <MButton
                        shape="circle"
                        variant="transparent"
                        class="small-icon-button"
                      >
                        <MIcon name="times" size="lg"></MIcon>
                      </MButton>
                    </template>
                    {{ $t('cancel_download') }}
                  </MTooltip>
                </template>
              </FlotoDeleteBtn>
              <MTooltip
                v-if="item.downloadState === 'completed'"
                placement="bottomRight"
              >
                <template v-slot:trigger>
                  <MButton variant="transparent" @click="handleDownload(item)">
                    <MIcon name="download" size="lg"></MIcon>
                  </MButton>
                </template>
                {{ $tc('download') }}
              </MTooltip>
            </span>
          </td>
        </template>
      </FlotoCrudContainer>
    </div>

    <template v-slot:actions="{ hide }">
      <MButton variant="default" @click="hide">
        {{ $t('close') }}
      </MButton>
    </template>
  </FlotoDrawer>
</template>

<script>
import { authComputed } from '@state/modules/auth'
import { downloadListColumns } from '@data/download'
import {
  getDownloadHistoryAPi,
  reDownloadApi,
  cancelDownloadApi,
} from '@modules/downloads/downloads-api'
import DownloadsFilter from './downloads-filter'
import moment from 'moment'
export default {
  name: 'DownloadsDrawer',
  components: {
    DownloadsFilter,
  },
  data() {
    return {
      isOpen: false,
      searchCriterias: {
        quals: [],
      },
      showTimeFilter: false,
      filterCriteria: {
        fromDate: 0,
        toDate: 0,
        createdById: 0,
      },
    }
  },
  computed: {
    ...authComputed,
    columns() {
      return downloadListColumns().filter((column) => {
        return column.key !== 'createdById'
      })
    },
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(val) {
        if (val) {
          this.filterCriteria.fromDate =
            moment().subtract(1, 'days').unix() * 1000
          this.filterCriteria.toDate = moment().unix() * 1000
          this.searchCriterias = {
            quals: [],
          }
        }
      },
    },
  },
  methods: {
    refresh() {
      this.$refs.crudContainer.refresh()
    },
    handleCancleDownlod(item) {
      return cancelDownloadApi(item.id).then(() => {
        this.refresh()
      })
    },
    handleDownload(item) {
      return reDownloadApi(item.id)
    },
    applyCriteria(searchCriteria, refreshList) {
      this.searchCriterias = searchCriteria
      refreshList()
    },

    handleOpen() {
      this.isOpen = true
    },

    handleFilterChange(filterData) {
      const { fromDate, toDate, createdById } = filterData
      this.filterCriteria = {
        fromDate,
        toDate,
        createdById,
      }
      this.showTimeFilter = false
      this.refresh()
    },

    getDownloads(limit, offset) {
      return getDownloadHistoryAPi(
        {
          createdById: this.user.id,
          ...(this.filterCriteria &&
          this.filterCriteria.fromDate &&
          this.filterCriteria.toDate
            ? {
                fromDate: this.filterCriteria.fromDate,
                toDate: this.filterCriteria.toDate,
              }
            : {}),
        },
        this.searchCriterias,
        limit,
        offset
      )
    },
  },
}
</script>
